import {Injectable} from '@angular/core';
import {DcpDataService} from './dcp-data.service';
import {CxEvent, Facet, ProductSearchService} from '@spartacus/core';
import {AdobeTrackingData} from './models/adobe-tracking-data';
import {Observable, of} from 'rxjs';
import {NavigationEvent, SearchPageResultsEvent} from '@spartacus/storefront';
import {first, map} from 'rxjs/operators';
import {ProductSearchPage} from '@spartacus/core/src/model/product-search.model';
import {DcpPageHitDataService} from './dcp-page-hit-data.service';
import {AdobeSearchFilter} from './search-data-tracking.service';
import {DcpFormatDataService} from './dcp-format-data.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

interface NavigationProductSearchTuple {
  navigation: NavigationEvent;
  productSearch?: ProductSearchPage;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DcpSearchPageHitService extends DcpDataService<AdobeTrackingData> {

  private isSearchSuggestion = false;

  paddingBase: string = 'https://example.com';
  lastNavigationEventBuffer: NavigationProductSearchTuple[];

  constructor(
    private productSearch: ProductSearchService,
    private pageHitData: DcpPageHitDataService,
    private formatDataService: DcpFormatDataService,
  ) {
    super();
    this.lastNavigationEventBuffer = [];
  }

  fillData(event: SearchPageResultsEvent, data?: AdobeTrackingData): AdobeTrackingData {
    if (!data) {
      data = {
        events: {},
      };
    }
    if (this.shouldTrackNavigation()) {
      this.pageHitData.fillData(event.navigation, data);
      if (!data.page) {
        data.page = {
          url: '',
          referrer: '',
        };
      }
      data.page.url = this.formatDataService.formatUrl(this.stripSearchUrl(window.location.href));
    }
    if (!this.hasMatchingSearchTerm(this.lastNavigationEventBuffer[0]?.navigation, this.lastNavigationEventBuffer[1]?.navigation)) {
      data.events.search = true;
      if (!data.search) {
        data.search = {};
      }
      data.search.term = this.formatSearchTerm(event.searchTerm);
      data.search.type = this.isSearchSuggestion ? 'search suggestion' : 'keyword search';
      this.isSearchSuggestion = false;
      if (this.shouldTrackSearchNoResults(event)) {
        data.events.searchNoResults = true;
      }
    }
    const newFilters: AdobeSearchFilter[] = this.getNewFilters(this.lastNavigationEventBuffer[0]?.productSearch, this.lastNavigationEventBuffer[1]?.productSearch);
    if (newFilters.length) {
      if (!data.search) {
        data.search = {};
      }
      data.events.searchFilter = true;
      data.search.filters = newFilters;
    }
    return Object.keys(data.events).length ? data : undefined;
  }

  getData(event: CxEvent): Observable<AdobeTrackingData> {
    if (event instanceof SearchPageResultsEvent) {
      return this.productSearch.getResults().pipe(first(), map<ProductSearchPage, AdobeTrackingData>((productSearchPage) => {
        if (this.lastNavigationEventBuffer[0]) {
          this.lastNavigationEventBuffer[0].productSearch = productSearchPage;
        }
        return this.fillData(event);
      }), untilDestroyed(this));
    } else if (event instanceof NavigationEvent) {
      this.lastNavigationEventBuffer = this.addToBuffer<NavigationProductSearchTuple>({navigation: event}, this.lastNavigationEventBuffer, 2);
    }
    return of(undefined);
  }

  public setIsSearchSuggestion(): void {
    this.isSearchSuggestion = true;
  }

  protected addToBuffer<T>(data: T, buffer: T[], limit: number): T[] {
    buffer.unshift(data);
    if (buffer.length > limit) {
      buffer = buffer.slice(0, limit);
    }
    return buffer;
  }

  protected formatSearchTerm(term: string): string {
    return term.substr(0, 99).toLowerCase();
  }

  protected shouldTrackNavigation(): boolean {
    return !this.hasMatchingSearchTerm(this.lastNavigationEventBuffer[0]?.navigation, this.lastNavigationEventBuffer[1]?.navigation)
      || !this.hasMatchingPagination(this.lastNavigationEventBuffer[0]?.navigation, this.lastNavigationEventBuffer[1]?.navigation);
  }

  protected shouldTrackSearchNoResults(event: SearchPageResultsEvent): boolean {
    return +event.numberOfResults <= 0;
  }

  protected hasMatchingSearchTerm(newEvent: NavigationEvent, oldEvent: NavigationEvent): boolean {
    const newQuery = newEvent?.params?.query;
    const oldQuery = oldEvent?.params?.query;
    return newEvent !== undefined && oldEvent !== undefined && newQuery === oldQuery
      || (newEvent === undefined && oldEvent === undefined);
  }

  protected getNewFilters(newProductSearch: ProductSearchPage, oldProductSearch: ProductSearchPage): AdobeSearchFilter[] {
    const newFilters = this.convertFacetsToFilters(newProductSearch?.facets);
    const oldFilters = this.convertFacetsToFilters(oldProductSearch?.facets);
    return newFilters.filter(t => !oldFilters.some((s) => s.name === t.name));
  }

  protected hasMatchingPagination(newEvent: NavigationEvent, oldEvent: NavigationEvent): boolean {
    const newPagination = this.getCurrentPageFromUrl(newEvent?.url);
    const oldPagination = this.getCurrentPageFromUrl(oldEvent?.url);
    return newPagination === undefined || oldPagination === undefined || newPagination === oldPagination;
  }

  protected getCurrentPageFromUrl(url: string): string {
    return new URL(url, this.paddingBase).searchParams.get('currentPage') || '0';
  }

  protected convertFacetsToFilters(facets: Facet[]): AdobeSearchFilter[] {
    if (!facets) {
      return [];
    }
    return facets.reduce((facetAcc, facet) => [...facetAcc, ...facet.values.filter((value) => value.selected).map((value) => ({
      category: facet.name,
      name: value.name
    }))], []);
  }

  protected stripSearchUrl(url: string): string {
    return url.replace(/(.*search).*/, '$1');
  }
}
